import React, { useState, useEffect, useCallback } from 'react';
import { Card, Modal, Alert, Badge, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { Select, Input, DataTable } from '../../../../components';
import Button from '../../../../components/common/Button';
import { getDriverTierProgress, getDriverTierLogs, assignTierToDriver } from '../../../../api/driver-tier';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { DATETIME_FORMAT } from '../../../../shared/constants';
import { RiArrowUpLine, RiArrowDownLine, RiCheckLine } from 'react-icons/ri';
import { BsChevronRight } from 'react-icons/bs';

const DriverTier = ({ driverId, currentTier, onTierChanged }) => {
    const { t } = useTranslation();
    const [tiers, setTiers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tierProgressLoading, setTierProgressLoading] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [tierProgress, setTierProgress] = useState(null);

    const form = useForm({
        defaultValues: {
            tierId: currentTier?.id || '',
            reason: ''
        }
    });

    const loadTierProgress = useCallback(async () => {
        try {
            setTierProgressLoading(true);
            const response = await getDriverTierProgress(driverId);
            setTierProgress(response || null);
            setTiers(response?.tiers || []);
        } catch (error) {
            console.error('Error loading tier progress:', error);
            toast.error(t('FAILED_TO_LOAD_TIER_PROGRESS'));
        } finally {
            setTierProgressLoading(false);
        }
    }, [driverId, t]);

    const loadLogs = useCallback(async () => {
        try {
            setLogsLoading(true);
            const response = await getDriverTierLogs({
                driverId,
                skip: 0,
                limit: 50
            });
            setLogs(response.items || []);
        } catch (error) {
            console.error('Error loading logs:', error);
            toast.error(t('FAILED_TO_LOAD_TIER_LOGS'));
        } finally {
            setLogsLoading(false);
        }
    }, [driverId, t]);

    useEffect(() => {
        loadTierProgress();
        loadLogs();
    }, [loadTierProgress, loadLogs]);

    useEffect(() => {
        const tierId = tierProgress?.currentTier?.id || currentTier?.id;
        if (tierId) {
            form.setValue('tierId', tierId);
        }
    }, [tierProgress, currentTier, form]);

    const handleAssignTier = async (data) => {
        try {
            setLoading(true);
            await assignTierToDriver(driverId, data.tierId, data.reason);
            setShowAssignModal(false);
            form.reset({ tierId: data.tierId, reason: '' });
            loadTierProgress();
            loadLogs();
            if (onTierChanged) {
                onTierChanged();
            }
        } catch (error) {
            console.error('Error assigning tier:', error);
            toast.error(t('FAILED_TO_ASSIGN_TIER'));
        } finally {
            setLoading(false);
        }
    };

    const getActionBadge = (action) => {
        const badges = {
            'ASSIGNED': <Badge bg="primary"><RiCheckLine /> {t('ASSIGNED')}</Badge>,
            'UPGRADED': <Badge bg="success"><RiArrowUpLine /> {t('UPGRADED')}</Badge>,
            'DOWNGRADED': <Badge bg="warning"><RiArrowDownLine /> {t('DOWNGRADED')}</Badge>,
            'TIER_UPDATED': <Badge bg="info">{t('TIER_UPDATED')}</Badge>,
        };
        return badges[action] || <Badge bg="secondary">{action}</Badge>;
    };

    const tierOptions = tiers.map(tier => ({
        value: tier.id,
        label: `${tier.tierName} (Level ${tier.tierLevel})`
    }));

    const resolvedCurrentTier = tierProgress?.currentTier || currentTier;
    const selectedTierId = form.watch('tierId');
    const isSameTier = selectedTierId === resolvedCurrentTier?.id;
    const progressSteps = tierProgress?.tiers || tiers;
    const nextTier = tierProgress?.nextTier;
    const nextTierRuleProgress = tierProgress?.progress?.rules?.[0];
    const progressPercentage = nextTierRuleProgress?.threshold > 0
        ? Math.min(Math.round((nextTierRuleProgress.currentValue / nextTierRuleProgress.threshold) * 100), 100)
        : 0;
    const hasCurrentTier = Boolean(resolvedCurrentTier?.id);
    const tierRuleList = [...(progressSteps || [])].sort((a, b) => b.tierLevel - a.tierLevel);
    const maxRuleCount = tierRuleList.reduce((acc, tier) => Math.max(acc, tier?.upgradeRules?.length || 0), 0);

    const ruleTypeLabels = {
        order_count: 'UPGRADE_RULE_ORDER_COUNT',
        total_sales: 'UPGRADE_RULE_TOTAL_SALES',
        rating: 'UPGRADE_RULE_RATING',
    };
    const rulePeriodLabels = {
        all_time: 'UPGRADE_RULE_PERIOD_ALL_TIME',
        yearly: 'UPGRADE_RULE_PERIOD_YEARLY',
        monthly: 'UPGRADE_RULE_PERIOD_MONTHLY',
    };
    const ruleOperatorLabels = {
        gte: '>=',
        lte: '<=',
        eq: '=',
    };


    const nextTierRequirementText = nextTier
        ? (nextTierRuleProgress
            ? t('NEXT_TIER_REQUIREMENT', {
                tierName: nextTier.tierName,
                ruleType: t(ruleTypeLabels[nextTierRuleProgress.type]),
                amount: nextTierRuleProgress.threshold - nextTierRuleProgress.currentValue,
            })
            : t('NEXT_TIER_REQUIREMENT_FALLBACK', { tierName: nextTier.tierName }))
        : '';

    const renderProgressIndicator = ({ currentValue, threshold, percentage, barHeight = '4px' }) => {
        const safePercentage = Number.isFinite(percentage)
            ? Math.max(0, Math.min(Math.round(percentage), 100))
            : 0;
        const safeCurrentValue = Number(currentValue);
        const safeThreshold = Number(threshold);
        const hasValues = Number.isFinite(safeCurrentValue) && Number.isFinite(safeThreshold);

        return (
            <>
                <div className="d-flex justify-content-between">
                    <small className={safePercentage >= 100 ? 'text-success' : 'text-muted'}>
                        {hasValues ? `${safeCurrentValue} / ${safeThreshold}` : '-'}
                    </small>
                    <small className={safePercentage >= 100 ? 'text-success' : 'text-primary'}>
                        {safePercentage >= 100 ? t('COMPLETED') : `${safePercentage}%`}
                    </small>
                </div>
                <div className="progress mt-1" style={{ height: barHeight }}>
                    <div
                        className={`progress-bar ${safePercentage >= 100 ? 'bg-success' : 'bg-primary'}`}
                        role="progressbar"
                        style={{ width: `${safePercentage}%` }}
                    />
                </div>
            </>
        );
    };

    const logColumns = [
        {
            key: 'createdAt',
            label: t('DATE'),
            render: (value) => dayjs(value).format(DATETIME_FORMAT)
        },
        {
            key: 'action',
            label: t('ACTION'),
            render: (value) => getActionBadge(value)
        },
        {
            key: 'oldTierData',
            label: t('OLD_TIER'),
            render: (value) => value ? (
                <>
                    <div className="fw-bold">{value.tierName}</div>
                    <small className="text-muted">Level {value.tierLevel}</small>
                </>
            ) : (
                <span className="text-muted">-</span>
            )
        },
        {
            key: 'newTierData',
            label: t('NEW_TIER'),
            render: (value) => value ? (
                <>
                    <div className="fw-bold">{value.tierName}</div>
                    <small className="text-muted">Level {value.tierLevel}</small>
                </>
            ) : (
                <span className="text-muted">-</span>
            )
        },
        {
            key: 'reason',
            label: t('REASON'),
            render: (value) => value || <span className="text-muted">-</span>
        },
        {
            key: 'createdBy',
            label: t('PERFORMED_BY'),
            render: (value) => value?.fullName || <span className="text-muted">{t('SYSTEM')}</span>
        }
    ];

    return (
        <div className="mt-4">
            {/* Current Tier Card */}
            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h5 className="mb-1">{t('CURRENT_TIER')}</h5>
                            {resolvedCurrentTier ? (
                                <>
                                    <h3 className="text-primary mb-2">{resolvedCurrentTier.tierName}</h3>
                                    <div className="text-muted">
                                        <div><strong>{t('TIER_LEVEL')}:</strong> {resolvedCurrentTier.tierLevel}</div>
                                        <div><strong>{t('DRIVER_COMMISSION')}:</strong> {resolvedCurrentTier.driverCommissionPercentage}%</div>
                                        <div><strong>{t('COMPANY_COMMISSION')}:</strong> {resolvedCurrentTier.companyCommissionPercentage}%</div>
                                    </div>
                                </>
                            ) : (
                                <Alert variant="warning" className="mb-0 mt-2">
                                    {t('NO_TIER_ASSIGNED')}
                                </Alert>
                            )}
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => setShowAssignModal(true)}
                        >
                            {resolvedCurrentTier ? t('CHANGE_TIER') : t('ASSIGN_TIER')}
                        </Button>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                            className="mb-4 p-3 border rounded position-relative"
                            style={{ backgroundColor: '#f8f9fa', cursor: 'pointer' }}
                            onClick={() => setShowRulesModal(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowRulesModal(true); }}
                        >
                            <div className="d-flex align-items-center justify-content-between">
                                <h6 className="mb-2">{t('TIER_PROGRESS')}</h6>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="position-absolute top-0 end-0 m-2 p-1 rounded"
                                    style={{ minWidth: '32px' }}
                                    onClick={(e) => { e.stopPropagation(); setShowRulesModal(true); }}
                                    aria-label={t('VIEW_TIER_RULES_AND_RATES')}
                                >
                                    <BsChevronRight size={20} />
                                </Button>
                            </div>
                            {tierProgressLoading ? (
                                <div className="text-muted">{t('LOADING')}...</div>
                            ) : progressSteps.length === 0 ? (
                                <Alert variant="light" className="mb-0">
                                    {t('NO_TIER_ASSIGNED')}
                                </Alert>
                            ) : (
                                <>
                                    <div className="overflow-auto pb-2">
                                        <div
                                            className="d-flex align-items-start"
                                            style={{ minWidth: `${Math.max(progressSteps.length * 160, 560)}px` }}
                                        >
                                            {progressSteps.map((tier, index) => {
                                                const isCompleted = tier.isPassed || tier.isCurrent;
                                                const nextStep = progressSteps[index + 1];
                                                const isConnectorActive = isCompleted && (nextStep?.isPassed || nextStep?.isCurrent);

                                                return (
                                                    <div
                                                        key={tier.id}
                                                        className="position-relative d-flex flex-column align-items-center flex-grow-1"
                                                        style={{ width: '100%', minWidth: 0 }}
                                                    >
                                                        {index < progressSteps.length - 1 && (
                                                            <span
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '4px',
                                                                    left: '50%',
                                                                    width: '100%',
                                                                    height: '4px',
                                                                    backgroundColor: isConnectorActive ? '#0d6efd' : '#dee2e6',
                                                                    zIndex: 0,
                                                                }}
                                                            />
                                                        )}
                                                        <div
                                                            className="d-flex align-items-center justify-content-center fw-bold"
                                                            style={{
                                                                width: '12px',
                                                                height: '12px',
                                                                borderRadius: '50%',
                                                                backgroundColor: isCompleted ? '#0d6efd' : '#dee2e6',
                                                                color: isCompleted ? '#ffffff' : '#6c757d',
                                                                border: tier.isCurrent ? '2px solid #0a58ca' : '2px solid #ced4da',
                                                                zIndex: 1,
                                                            }}
                                                        >
                                                        </div>
                                                        <div className="text-center mt-2">
                                                            <div className={`fw-semibold ${tier.isCurrent ? 'text-dark' : 'text-muted'}`}>
                                                                {tier.tierName}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        {nextTier ? (
                                            <>
                                                <small className="text-muted d-block mb-1">
                                                    {nextTierRequirementText}
                                                </small>
                                                {renderProgressIndicator({
                                                    currentValue: nextTierRuleProgress?.currentValue,
                                                    threshold: nextTierRuleProgress?.threshold,
                                                    percentage: progressPercentage,
                                                    barHeight: '6px',
                                                })}
                                            </>
                                        ) : hasCurrentTier ? (
                                            <small className="text-success">{t('TOP_TIER_REACHED')}</small>
                                        ) : null}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Tier Change History */}
            <Card>
                <Card.Header>
                    <h5 className="mb-0">{t('TIER_CHANGE_HISTORY')}</h5>
                </Card.Header>
                <Card.Body>
                    {logs.length === 0 && !logsLoading ? (
                        <Alert variant="info">{t('NO_TIER_HISTORY')}</Alert>
                    ) : (
                        <DataTable
                            columns={logColumns}
                            data={logs}
                            loading={logsLoading}
                        />
                    )}
                </Card.Body>
            </Card>

            {/* Tier Rules & Rates Modal */}
            <Modal show={showRulesModal} onHide={() => setShowRulesModal(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('TIER_RULES_AND_RATES')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tierRuleList.length === 0 ? (
                        <Alert variant="info" className="mb-0">
                            {t('NO_DATA')}
                        </Alert>
                    ) : (
                        <div className="overflow-auto pb-2">
                            <Table
                                bordered
                                responsive
                                className="mb-0 align-middle"
                                style={{ minWidth: `${Math.max(1500, 220 + (tierRuleList.length * 240))}px` }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '220px' }}>{t('MATRIX_ITEM')}</th>
                                        {tierRuleList.map((tier) => {
                                            return (
                                                <th
                                                    key={tier.id}
                                                    style={{
                                                        minWidth: '240px',
                                                        backgroundColor: tier.isCurrent ? '#eef8fb' : '#ffffff',
                                                    }}
                                                >
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-semibold">{tier.tierName}</span>
                                                        <small className="text-muted">{t('TIER_LEVEL')} {tier.tierLevel}</small>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">{t('TIER_PROGRESS')}</td>
                                        {tierRuleList.map((tier) => {
                                            const isNextTier = nextTier?.id === tier.id;
                                            const nextTierRule = tierProgress?.progress?.rules?.[0];
                                            const isDoneTier = !tier.isCurrent && tier.isPassed;
                                            const statusLabel = tier.isCurrent ? 'CURRENT_TIER' : isDoneTier ? 'UNLOCK' : 'LOCKED';
                                            const statusVariant = tier.isCurrent ? 'primary' : isDoneTier ? 'success' : 'secondary';
                                            return (
                                                <td
                                                    key={`progress-${tier.id}`}
                                                    style={{ backgroundColor: tier.isCurrent ? '#f8fcff' : '#ffffff' }}
                                                >
                                                    {isNextTier ? (
                                                        <>
                                                            <div className="mb-1">
                                                                <Badge bg={progressPercentage >= 100 ? 'success' : 'secondary'}>
                                                                    {t('LOCKED')}
                                                                </Badge>
                                                            </div>
                                                            {renderProgressIndicator({
                                                                currentValue: nextTierRule?.currentValue,
                                                                threshold: nextTierRule?.threshold,
                                                                percentage: progressPercentage,
                                                            })}

                                                        </>
                                                    ) : (
                                                        <Badge bg={statusVariant}>{t(statusLabel)}</Badge>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">{t('DRIVER_COMMISSION')}</td>
                                        {tierRuleList.map((tier) => (
                                            <td
                                                key={`driver-commission-${tier.id}`}
                                                style={{ backgroundColor: tier.isCurrent ? '#f8fcff' : '#ffffff' }}
                                            >
                                                {tier.driverCommissionPercentage}%
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">{t('COMPANY_COMMISSION')}</td>
                                        {tierRuleList.map((tier) => (
                                            <td
                                                key={`company-commission-${tier.id}`}
                                                style={{ backgroundColor: tier.isCurrent ? '#f8fcff' : '#ffffff' }}
                                            >
                                                {tier.companyCommissionPercentage}%
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">{t('DESCRIPTION')}</td>
                                        {tierRuleList.map((tier) => (
                                            <td
                                                key={`description-${tier.id}`}
                                                style={{ backgroundColor: tier.isCurrent ? '#f8fcff' : '#ffffff' }}
                                            >
                                                {tier.description || '-'}
                                            </td>
                                        ))}
                                    </tr>
                                    {Array.from({ length: maxRuleCount }, (_, idx) => idx).map((ruleIndex) => (
                                        <tr key={`rule-row-${ruleIndex}`}>
                                            <td className="fw-semibold">{t('RULE')} {ruleIndex + 1}</td>
                                            {tierRuleList.map((tier) => {
                                                const rule = tier?.upgradeRules?.[ruleIndex];
                                                const thresholdValue = Number(rule?.threshold ?? 0);

                                                return (
                                                    <td
                                                        key={`rule-${tier.id}-${ruleIndex}`}
                                                        style={{ backgroundColor: tier.isCurrent ? '#f8fcff' : '#ffffff' }}
                                                    >
                                                        {rule ? (
                                                            <>
                                                                {t(ruleTypeLabels[rule.type])} {ruleOperatorLabels?.[rule.operator]} {thresholdValue} within {t(rulePeriodLabels[rule.period])}
                                                            </>
                                                        ) : "-"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRulesModal(false)}>
                        {t('CLOSE')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Assign/Change Tier Modal */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{resolvedCurrentTier ? t('CHANGE_TIER') : t('ASSIGN_TIER')}</Modal.Title>
                </Modal.Header>
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(handleAssignTier)}>
                        <Modal.Body>
                            <Select
                                label={t('SELECT_TIER')}
                                name="tierId"
                                options={tierOptions}
                                rules={{ required: t('TIER_IS_REQUIRED') }}
                            />
                            <Input
                                label={t('REASON')}
                                name="reason"
                                type="textarea"
                                rows={3}
                                placeholder={t('REASON_FOR_TIER_CHANGE')}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowAssignModal(false)}
                                disabled={loading}
                            >
                                {t('CANCEL')}
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={loading}
                                disabled={loading || isSameTier}
                            >
                                {t('SAVE')}
                            </Button>
                        </Modal.Footer>
                    </form>
                </FormProvider>
            </Modal>
        </div>
    );
};

export default DriverTier;

